// DeclarePage (v0.1 redesign — workbench redesign doc §5):
//
// Per-row column-declaration form, sourced from the workbench session's
// column_pool. One row per picked column; each row captures the AC
// author's intent (ac_name, kind, ip_reducer, anchor A, anchor M,
// description, cache_hint). Multiple rows may share an ac_name —
// rows sharing a name form one AC family with multiple anchor-locked
// members.
//
// Below the per-row grid: separate sections for derived metrics
// (formula over other AC names) and derived dimensions (mapping from
// another dim).
//
// S2 scope: capture + persist + restore. Does NOT yet compile into the
// session's draft_ac (the Verify / Commit / Emit pipeline still
// operates on draft_ac for now). S3 closes the loop with a Compile
// step + the consistency checks from redesign doc §5.4.

import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import type { ProposalRow } from "../api/client";
import type {
  CacheHintValue,
  ColumnDeclaration,
  ColumnRef,
  DeclareKind,
  DerivedDimensionSpecForm,
  DerivedMetricSpecForm,
  SessionInfo,
} from "../api/types";
import { useSession } from "./SessionContext";

// ── Form-state types (loose strings; backend Pydantic mirrors them) ───

interface RowFormState extends ColumnDeclaration {
  // Convenience flag — true once the user has modified this row from
  // its empty default. Used to drive "save dirty rows only" later if we
  // want to optimise; for S2 we just round-trip the whole list.
  touched: boolean;
}

// ── Page ──────────────────────────────────────────────────────────────

export function DeclarePage() {
  const { client, session, refreshSession } = useSession();

  // Per-pool-column declarations, keyed by refKey(column_ref).
  const [rows, setRows] = useState<Map<string, RowFormState>>(new Map());
  const [derivedMetrics, setDerivedMetrics] = useState<DerivedMetricSpecForm[]>(
    [],
  );
  const [derivedDims, setDerivedDims] = useState<DerivedDimensionSpecForm[]>(
    [],
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  // Non-fatal: set when the Discover proposals couldn't be fetched, so the
  // page falls back to hint-only defaults but tells the author why.
  const [proposalError, setProposalError] = useState<string | null>(null);

  // On mount (or session change), pre-fill the row map from the Discover
  // proposals — the Python `propose_column_specs` output is the single
  // source of truth for category, grain (anchor A), and family reducer
  // (ADR-023 L1). Declare no longer reimplements grain inference in TS;
  // it ratifies what Discover proposed.
  //
  // Flow:
  //   1. fetch persisted proposals (GET .../proposals)
  //   2. if none cached but columns are pooled, run DiscoverColumnSpecs
  //      once so the page pre-fills instead of falling back to bare hints
  //   3. build the row map from the proposals; columns with no proposal
  //      fall back to a hint-only default (kind from kind_hint, empty
  //      anchors)
  //   4. overlay persisted column_declarations last — the author's saved
  //      edits always win
  //
  // Proposal fetch failure is non-fatal: the page still renders with
  // hint-only defaults and surfaces `proposalError`.
  useEffect(() => {
    if (session === null) {
      setRows(new Map());
      setDerivedMetrics([]);
      setDerivedDims([]);
      setDirty(false);
      return;
    }
    const s = session;
    let cancelled = false;

    async function load() {
      let proposalRows: ProposalRow[] = [];
      try {
        const resp = await client.getProposals(s.session_id);
        proposalRows = resp.rows;
        if (proposalRows.length === 0 && s.column_pool_size > 0) {
          const r = await client.runOp(s.session_id, "DiscoverColumnSpecs", {
            inputs: {},
          });
          if (r.success && r.result) {
            proposalRows = (r.result as { rows?: ProposalRow[] }).rows ?? [];
          }
        }
        if (!cancelled) setProposalError(null);
      } catch (e: unknown) {
        if (!cancelled) setProposalError(formatError(e));
      }
      if (cancelled) return;
      setRows(buildRowMap(s, proposalRows));
      setDerivedMetrics(s.derived_metric_specs.map((d) => ({ ...d })));
      setDerivedDims(s.derived_dimension_specs.map((d) => ({ ...d })));
      setDirty(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session?.session_id,
    session?.column_pool_size,
    session?.pool_column_hints,
    session?.column_declarations,
  ]);

  const acNames = useMemo(() => collectAcNames(rows, derivedMetrics, derivedDims), [
    rows,
    derivedMetrics,
    derivedDims,
  ]);

  // dtype lookup keyed by `table|column`, sourced from the pool's
  // quasi-metadata hints. Drives the (display-only) dtype cell — the
  // ColumnRef itself doesn't carry dtype, so we read it off the session.
  const dtypeByPhysCol = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of session?.pool_column_hints ?? []) {
      m.set(`${h.table}|${h.column}`, h.dtype);
    }
    return m;
  }, [session?.pool_column_hints]);

  // Group the declaration rows by physical table — one displayed segment
  // per table, mirroring the Columns page. Table name becomes the section
  // header (not a per-row column); the schema column is gone entirely.
  const rowsByTable = useMemo(() => {
    const byTable = new Map<string, RowFormState[]>();
    for (const row of rows.values()) {
      const t = row.column_ref.table;
      const arr = byTable.get(t);
      if (arr) arr.push(row);
      else byTable.set(t, [row]);
    }
    for (const arr of byTable.values()) {
      arr.sort((a, b) =>
        a.column_ref.column.localeCompare(b.column_ref.column),
      );
    }
    return Array.from(byTable.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([table, rs]) => ({ table, rows: rs }));
  }, [rows]);

  function updateRow(key: string, patch: Partial<ColumnDeclaration>) {
    setRows((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      if (cur === undefined) return prev;
      next.set(key, { ...cur, ...patch, touched: true });
      return next;
    });
    setDirty(true);
    setSavedMessage(null);
  }

  function updateDerivedMetric(idx: number, patch: Partial<DerivedMetricSpecForm>) {
    setDerivedMetrics((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );
    setDirty(true);
    setSavedMessage(null);
  }

  function addDerivedMetric() {
    setDerivedMetrics((prev) => [
      ...prev,
      {
        ac_name: "",
        formula: "",
        anchor_a: [],
        description: "",
        cache_hint: "",
      },
    ]);
    setDirty(true);
    setSavedMessage(null);
  }

  function removeDerivedMetric(idx: number) {
    setDerivedMetrics((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
    setSavedMessage(null);
  }

  function updateDerivedDim(idx: number, patch: Partial<DerivedDimensionSpecForm>) {
    setDerivedDims((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );
    setDirty(true);
    setSavedMessage(null);
  }

  function addDerivedDim() {
    setDerivedDims((prev) => [
      ...prev,
      {
        ac_name: "",
        derived_from: "",
        mapping: {},
        default: "",
        description: "",
      },
    ]);
    setDirty(true);
    setSavedMessage(null);
  }

  function removeDerivedDim(idx: number) {
    setDerivedDims((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
    setSavedMessage(null);
  }

  async function saveAll() {
    if (session === null) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);

    // Strip the touched flag and rows that are completely empty (no
    // ac_name, no kind, no description) — those represent pool entries
    // the user hasn't touched yet, no need to persist them.
    const declarations: ColumnDeclaration[] = [];
    for (const row of rows.values()) {
      if (
        row.ac_name === "" &&
        row.kind === "" &&
        row.ip_reducer === "" &&
        row.anchor_a.length === 0 &&
        row.anchor_m.length === 0 &&
        row.naa_blocks.length === 0 &&
        row.description === "" &&
        row.cache_hint === ""
      ) {
        continue;
      }
      const { touched: _touched, ...rest } = row;
      declarations.push(rest);
    }

    try {
      const r = await client.runOp(
        session.session_id,
        "SetColumnDeclarations",
        {
          inputs: {
            column_declarations: declarations,
            derived_metric_specs: derivedMetrics,
            derived_dimension_specs: derivedDims,
          },
        },
      );
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "SetColumnDeclarations failed");
      }
      const result = r.result as {
        column_declaration_count: number;
        derived_metric_count: number;
        derived_dimension_count: number;
      };
      setSavedMessage(
        `Saved — ${result.column_declaration_count} columns` +
          `, ${result.derived_metric_count} derived metrics` +
          `, ${result.derived_dimension_count} derived dimensions.`,
      );
      setDirty(false);
      refreshSession();
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  }

  // ── Empty-state guards ──────────────────────────────────────────────

  if (session === null) {
    return (
      <section>
        <h1>Declare</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>Session</em> tab to start declaring.
        </p>
      </section>
    );
  }

  if (session.column_pool_size === 0) {
    return (
      <section>
        <h1>Declare</h1>
        <div style={emptyStateStyle}>
          <p>
            No columns in the pool yet. Pick the columns this AC should
            cover on the <strong>Column Pool</strong> tab first; this page
            then shows one row per picked column for you to declare.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Declare</h1>
          <p style={subheaderStyle}>
            One row per pool column. Multiple rows may share an{" "}
            <em>AC name</em> — that's the natural family relationship
            (multiple anchor-locked physical bindings under one AC family).
          </p>
        </div>
        <div style={statsStyle}>
          <Stat label="pool" value={String(session.column_pool_size)} />
          <Stat label="ac names" value={String(acNames.length)} />
          <Stat
            label="derived"
            value={String(derivedMetrics.length + derivedDims.length)}
          />
        </div>
      </header>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}
      {savedMessage && (
        <div style={savedBlockStyle}>{savedMessage}</div>
      )}
      {proposalError && (
        <div style={noticeBlockStyle}>
          Couldn't load Discover proposals ({proposalError}) — rows are
          pre-filled from column hints only. Run <strong>Discover</strong> to
          get full grain / reducer suggestions.
        </div>
      )}

      <div style={topActionsStyle}>
        <span style={{ color: "#666", fontSize: "0.9rem" }}>
          {dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <button
          onClick={saveAll}
          style={dirty ? primaryButtonStyle : primaryButtonDisabledStyle}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save declarations"}
        </button>
      </div>

      <h2 style={sectionHeaderStyle}>Physical columns</h2>
      {rowsByTable.map(({ table, rows: tableRows }) => (
        <div key={table} style={tableSectionStyle}>
          <div style={tableSectionHeaderStyle}>
            <code style={tableSectionNameStyle}>{table}</code>
            <span style={tableSectionMetaStyle}>
              {tableRows.length}{" "}
              {tableRows.length === 1 ? "column" : "columns"}
            </span>
          </div>
          <div style={tableWrapperStyle}>
            <table style={gridStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>column</th>
                  <th style={thStyle}>type</th>
                  <th style={thMandatoryStyle}>AC name *</th>
                  <th style={thStyle}>kind</th>
                  <th style={thStyle}>anchor A</th>
                  <th style={thStyle}>anchor M</th>
                  <th style={thStyle}>reducer</th>
                  <th style={thStyle}>NAA blocks</th>
                  <th style={thStyle}>cache</th>
                  <th style={thStyle}>description</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  const key = refKey(row.column_ref);
                  return (
                    <RowEditor
                      key={key}
                      row={row}
                      dtype={
                        dtypeByPhysCol.get(
                          `${row.column_ref.table}|${row.column_ref.column}`,
                        ) ?? ""
                      }
                      acNames={acNames}
                      onPatch={(patch) => updateRow(key, patch)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <h2 style={sectionHeaderStyle}>Derived metrics</h2>
      <p style={sectionHelpStyle}>
        AC-declared metrics with a formula over other AC names (no physical
        column ref). Example: <code>profit = revenue - cost</code>.
      </p>
      <DerivedMetricsTable
        items={derivedMetrics}
        acNames={acNames}
        onPatch={updateDerivedMetric}
        onAdd={addDerivedMetric}
        onRemove={removeDerivedMetric}
      />

      <h2 style={sectionHeaderStyle}>Derived dimensions</h2>
      <p style={sectionHelpStyle}>
        AC-declared dimensions computed via a value-by-value mapping from a
        source dimension. Example: <code>continent = MAP({"{East:NA,…}"})</code>.
      </p>
      <DerivedDimensionsTable
        items={derivedDims}
        acNames={acNames}
        onPatch={updateDerivedDim}
        onAdd={addDerivedDim}
        onRemove={removeDerivedDim}
      />

      <div style={{ ...topActionsStyle, marginTop: "2rem" }}>
        <span style={{ color: "#666", fontSize: "0.9rem" }}>
          {dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <button
          onClick={saveAll}
          style={dirty ? primaryButtonStyle : primaryButtonDisabledStyle}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save declarations"}
        </button>
      </div>

      <p style={{ color: "#888", fontSize: "0.85rem", marginTop: "1rem" }}>
        <strong>Heads-up:</strong> S2 stops at capture + persist. Compile-
        to-draft-AC and the consistency checks (anchor coverage, family
        kind/IPR agreement, kind-conditional required fields) land in S3.
      </p>
    </section>
  );
}

// ── Row editor ────────────────────────────────────────────────────────

function RowEditor({
  row,
  dtype,
  acNames,
  onPatch,
}: {
  row: RowFormState;
  dtype: string;
  acNames: string[];
  onPatch: (patch: Partial<ColumnDeclaration>) => void;
}) {
  return (
    <tr style={row.touched ? trTouchedStyle : trStyle}>
      <td style={tdStyle}>
        <code style={{ fontWeight: 500 }}>{row.column_ref.column}</code>
      </td>
      <td style={tdStyle}>
        {/* dtype is display-only; sourced from the pool's QM hints. */}
        <span style={{ color: "#888" }}>{dtype || "—"}</span>
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={row.ac_name}
          onChange={(e) => onPatch({ ac_name: e.target.value })}
          style={inputStyle}
          placeholder={row.column_ref.column}
        />
      </td>
      <td style={tdStyle}>
        <select
          value={row.kind}
          onChange={(e) => onPatch({ kind: e.target.value as DeclareKind })}
          style={selectStyle}
        >
          <option value="">—</option>
          <option value="dim">dim</option>
          <option value="attribute">attribute</option>
          <option value="metric">metric</option>
        </select>
      </td>
      <td style={tdStyle}>
        <AnchorMultiSelect
          value={row.anchor_a}
          options={acNames}
          onChange={(v) => onPatch({ anchor_a: v })}
          placeholder="A names…"
        />
      </td>
      <td style={tdStyle}>
        <AnchorMultiSelect
          value={row.anchor_m}
          options={acNames}
          onChange={(v) => onPatch({ anchor_m: v })}
          placeholder="MVD names…"
        />
      </td>
      <td style={tdStyle}>
        {/* reducer (IPR) is always editable. Conditionally-required at S3
            compile (kind=metric must have one), but the author is
            free to set it on any row in the meantime. */}
        <select
          value={row.ip_reducer}
          onChange={(e) => onPatch({ ip_reducer: e.target.value })}
          style={selectStyle}
        >
          <option value="">—</option>
          <option value="SUM">SUM</option>
          <option value="AVG">AVG</option>
          <option value="MIN">MIN</option>
          <option value="MAX">MAX</option>
          <option value="COUNT">COUNT</option>
          <option value="COUNT_DISTINCT">COUNT_DISTINCT</option>
          <option value="NONE">NONE</option>
        </select>
      </td>
      <td style={tdStyle}>
        {/* NAA blocks — AC-names over which this metric's reducer is
            non-additive (semi-additive). Metric-only in the engine, but
            always editable; pre-filled from the proposal's reducer
            a_block (ADR-023, Manual §6.1). */}
        <AnchorMultiSelect
          value={row.naa_blocks}
          options={acNames}
          onChange={(v) => onPatch({ naa_blocks: v })}
          placeholder="NAA names…"
        />
      </td>
      <td style={tdStyle}>
        {/* cache hint is always editable. Only meaningful for
            kind=metric in the engine (Phase 8), but harmless on
            non-metric rows — the compile step drops it for non-metrics. */}
        <select
          value={row.cache_hint}
          onChange={(e) =>
            onPatch({ cache_hint: e.target.value as CacheHintValue })
          }
          style={selectStyle}
        >
          <option value="">—</option>
          <option value="none">none</option>
          <option value="prefer">prefer</option>
          <option value="pin">pin</option>
        </select>
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={row.description}
          onChange={(e) => onPatch({ description: e.target.value })}
          style={{ ...inputStyle, minWidth: 180 }}
          placeholder="optional"
        />
      </td>
    </tr>
  );
}

// ── Anchor multi-select ───────────────────────────────────────────────

function AnchorMultiSelect({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string[];
  options: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [pending, setPending] = useState<string>("");
  const candidates = useMemo(
    () =>
      options.filter(
        (o) => !value.includes(o) && o.toLowerCase().includes(pending.toLowerCase()),
      ),
    [options, value, pending],
  );

  function commit(name: string) {
    if (name.trim() === "") return;
    if (!value.includes(name)) onChange([...value, name]);
    setPending("");
  }

  return (
    <div style={chipBoxStyle}>
      {value.map((v) => (
        <span key={v} style={chipStyle}>
          {v}
          <button
            onClick={() => onChange(value.filter((x) => x !== v))}
            style={chipRemoveStyle}
            title="remove"
          >
            ×
          </button>
        </span>
      ))}
      <input
        list="anchor-options"
        type="text"
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(pending);
          } else if (e.key === "Backspace" && pending === "" && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(pending)}
        placeholder={value.length === 0 ? (placeholder ?? "") : ""}
        style={chipInputStyle}
      />
      {pending.length > 0 && candidates.length > 0 && (
        <div style={suggestionListStyle}>
          {candidates.slice(0, 6).map((c) => (
            <div
              key={c}
              style={suggestionItemStyle}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(c);
              }}
            >
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Derived metrics table ─────────────────────────────────────────────

function DerivedMetricsTable({
  items,
  acNames,
  onPatch,
  onAdd,
  onRemove,
}: {
  items: DerivedMetricSpecForm[];
  acNames: string[];
  onPatch: (idx: number, patch: Partial<DerivedMetricSpecForm>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}) {
  return (
    <div style={tableWrapperStyle}>
      <table style={gridStyle}>
        <thead>
          <tr>
            <th style={thMandatoryStyle}>AC name *</th>
            <th style={thMandatoryStyle}>formula *</th>
            <th style={thStyle}>anchor A</th>
            <th style={thStyle}>cache</th>
            <th style={thStyle}>description</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...tdStyle, color: "#888" }}>
                No derived metrics yet.{" "}
                <button onClick={onAdd} style={linkButtonStyle}>
                  + add
                </button>
              </td>
            </tr>
          )}
          {items.map((item, idx) => (
            <tr key={idx} style={trStyle}>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.ac_name}
                  onChange={(e) => onPatch(idx, { ac_name: e.target.value })}
                  style={inputStyle}
                  placeholder="profit"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.formula}
                  onChange={(e) => onPatch(idx, { formula: e.target.value })}
                  style={{ ...inputStyle, minWidth: 220 }}
                  placeholder="revenue - cost"
                />
              </td>
              <td style={tdStyle}>
                <AnchorMultiSelect
                  value={item.anchor_a}
                  options={acNames}
                  onChange={(v) => onPatch(idx, { anchor_a: v })}
                  placeholder="A names…"
                />
              </td>
              <td style={tdStyle}>
                <select
                  value={item.cache_hint}
                  onChange={(e) =>
                    onPatch(idx, { cache_hint: e.target.value as CacheHintValue })
                  }
                  style={selectStyle}
                >
                  <option value="">—</option>
                  <option value="none">none</option>
                  <option value="prefer">prefer</option>
                  <option value="pin">pin</option>
                </select>
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => onPatch(idx, { description: e.target.value })}
                  style={{ ...inputStyle, minWidth: 180 }}
                  placeholder="optional"
                />
              </td>
              <td style={tdStyle}>
                <button
                  onClick={() => onRemove(idx)}
                  style={removeButtonStyle}
                  title="remove this derived metric"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 0 && (
        <div style={{ marginTop: "0.5rem" }}>
          <button onClick={onAdd} style={ghostButtonStyle}>
            + add derived metric
          </button>
        </div>
      )}
    </div>
  );
}

// ── Derived dimensions table ──────────────────────────────────────────

function DerivedDimensionsTable({
  items,
  acNames,
  onPatch,
  onAdd,
  onRemove,
}: {
  items: DerivedDimensionSpecForm[];
  acNames: string[];
  onPatch: (idx: number, patch: Partial<DerivedDimensionSpecForm>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}) {
  return (
    <div style={tableWrapperStyle}>
      <table style={gridStyle}>
        <thead>
          <tr>
            <th style={thMandatoryStyle}>AC name *</th>
            <th style={thMandatoryStyle}>derived from *</th>
            <th style={thStyle}>mapping (key:value, comma-sep)</th>
            <th style={thStyle}>default</th>
            <th style={thStyle}>description</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...tdStyle, color: "#888" }}>
                No derived dimensions yet.{" "}
                <button onClick={onAdd} style={linkButtonStyle}>
                  + add
                </button>
              </td>
            </tr>
          )}
          {items.map((item, idx) => (
            <tr key={idx} style={trStyle}>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.ac_name}
                  onChange={(e) => onPatch(idx, { ac_name: e.target.value })}
                  style={inputStyle}
                  placeholder="continent"
                />
              </td>
              <td style={tdStyle}>
                <select
                  value={item.derived_from}
                  onChange={(e) =>
                    onPatch(idx, { derived_from: e.target.value })
                  }
                  style={selectStyle}
                >
                  <option value="">—</option>
                  {acNames.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={formatMapping(item.mapping)}
                  onChange={(e) =>
                    onPatch(idx, { mapping: parseMapping(e.target.value) })
                  }
                  style={{ ...inputStyle, minWidth: 240 }}
                  placeholder="East:NA, West:NA, EU:EMEA"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.default}
                  onChange={(e) => onPatch(idx, { default: e.target.value })}
                  style={inputStyle}
                  placeholder="(strict)"
                />
              </td>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => onPatch(idx, { description: e.target.value })}
                  style={{ ...inputStyle, minWidth: 180 }}
                  placeholder="optional"
                />
              </td>
              <td style={tdStyle}>
                <button
                  onClick={() => onRemove(idx)}
                  style={removeButtonStyle}
                  title="remove this derived dimension"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 0 && (
        <div style={{ marginTop: "0.5rem" }}>
          <button onClick={onAdd} style={ghostButtonStyle}>
            + add derived dimension
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function refKey(ref: ColumnRef): string {
  return `${ref.db_schema}|${ref.table}|${ref.column}`;
}

// Map a Discover proposal category onto the Declare kind enum.
//   metric    → metric
//   dimension → dim
//   (anything else, incl. grain keys without a category) → ""
function kindFromCategory(category: string): DeclareKind {
  if (category === "metric") return "metric";
  if (category === "dimension") return "dim";
  return "";
}

// Fallback kind mapping from a quasi-metadata kind_hint, used only for
// pool columns that have no Discover proposal.
function kindFromHint(h: string): DeclareKind {
  if (h === "measure") return "metric";
  if (h === "dimension" || h === "identifier" || h === "timestamp") return "dim";
  if (h === "text" || h === "boolean") return "attribute";
  return "";
}

// Build the per-pool-column declaration row map (ADR-023 L1).
//
// Each row is pre-filled from the Discover proposal for that physical
// column — the Python `propose_column_specs` output is the single source
// of truth for category, grain (anchor A), and family reducer. Columns
// with no proposal fall back to a hint-only default. Persisted
// `column_declarations` overlay last so the author's saved edits win.
function buildRowMap(
  session: SessionInfo,
  proposalRows: ProposalRow[],
): Map<string, RowFormState> {
  // Index hints, persisted ac_names, and proposals by `table|column`.
  const hintByPhysCol = new Map<string, string>();
  for (const h of session.pool_column_hints ?? []) {
    hintByPhysCol.set(`${h.table}|${h.column}`, h.kind_hint);
  }
  const acNameByPhysCol = new Map<string, string>();
  for (const decl of session.column_declarations) {
    const n = decl.ac_name.trim();
    if (n !== "") {
      acNameByPhysCol.set(`${decl.column_ref.table}|${decl.column_ref.column}`, n);
    }
  }
  const proposalByPhysCol = new Map<string, ProposalRow>();
  for (const p of proposalRows) {
    proposalByPhysCol.set(`${p.table}|${p.column}`, p);
  }

  // Translate a physical column name to its declared ac_name, defaulting
  // to the physical name (which is also the default ac_name in this pass).
  function translate(table: string, physCol: string): string {
    return acNameByPhysCol.get(`${table}|${physCol}`) ?? physCol;
  }
  // Translate a proposal anchor list (physical column names) into ac_names,
  // preserving every entry. translate() maps each physical name to its
  // ac_name (defaulting to the physical name). A grain key anchors on its
  // own name (self-by-name; ADR-024 — no "self" sentinel), so its A is just
  // [columnName] and passes through like any other dim. We do NOT strip the
  // own-column name — the Declare defaults must be a faithful copy of what
  // Discover shows (Huayin 2026-06-10: "content on Declare must default to
  // Discover").
  function translateAnchorList(table: string, list: string[] | null): string[] {
    if (!list) return [];
    return list.map((a) => translate(table, a));
  }

  // Default M-anchor for a Declare row. Under ADR-024 grain-key columns carry
  // an EMPTY M — grain presence is a build-time invariant (D-005), not runtime
  // MNAR — so there is no "self" special-case: M is taken verbatim (translated)
  // and grain keys (M=[]/null) default to empty.
  function anchorMDefault(table: string, M: string[] | null): string[] {
    if (M !== null && M.length > 0) return M.map((m) => translate(table, m));
    return [];
  }

  function rowFromProposal(ref: ColumnRef, p: ProposalRow): RowFormState {
    const kind = kindFromCategory(p.category);
    return {
      column_ref: ref,
      ac_name: ref.column,
      kind,
      // Mirror Discover's reducer cell: the proposed operator, or empty
      // (rendered "—") when the proposal has none.
      ip_reducer: p.reducer_operator ?? "",
      anchor_a: translateAnchorList(ref.table, p.A),
      anchor_m: anchorMDefault(ref.table, p.M),
      naa_blocks: translateAnchorList(ref.table, p.reducer_a_block),
      description: "",
      cache_hint: "",
      touched: false,
    };
  }

  function fallbackRow(ref: ColumnRef): RowFormState {
    const kind = kindFromHint(hintByPhysCol.get(`${ref.table}|${ref.column}`) ?? "");
    return {
      column_ref: ref,
      ac_name: ref.column,
      kind,
      ip_reducer: kind === "metric" ? "SUM" : "",
      anchor_a: [],
      anchor_m: [],
      naa_blocks: [],
      description: "",
      cache_hint: "",
      touched: false,
    };
  }

  const map = new Map<string, RowFormState>();
  for (const ref of session.column_pool) {
    const p = proposalByPhysCol.get(`${ref.table}|${ref.column}`);
    map.set(refKey(ref), p ? rowFromProposal(ref, p) : fallbackRow(ref));
  }
  // Persisted declarations win over inference. Default naa_blocks for
  // decls persisted before the field existed (older sessions).
  for (const decl of session.column_declarations) {
    map.set(refKey(decl.column_ref), {
      ...decl,
      naa_blocks: decl.naa_blocks ?? [],
      touched: false,
    });
  }
  return map;
}

function collectAcNames(
  rows: Map<string, RowFormState>,
  derivedMetrics: DerivedMetricSpecForm[],
  derivedDims: DerivedDimensionSpecForm[],
): string[] {
  const names = new Set<string>();
  for (const r of rows.values()) {
    if (r.ac_name.trim() !== "") names.add(r.ac_name.trim());
  }
  for (const d of derivedMetrics) {
    if (d.ac_name.trim() !== "") names.add(d.ac_name.trim());
  }
  for (const d of derivedDims) {
    if (d.ac_name.trim() !== "") names.add(d.ac_name.trim());
  }
  return Array.from(names).sort();
}

function formatMapping(m: Record<string, string>): string {
  return Object.entries(m)
    .map(([k, v]) => `${k}:${v}`)
    .join(", ");
}

function parseMapping(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of s.split(",")) {
    const p = pair.trim();
    if (p === "") continue;
    const idx = p.indexOf(":");
    if (idx === -1) continue;
    const k = p.slice(0, idx).trim();
    const v = p.slice(idx + 1).trim();
    if (k !== "") out[k] = v;
  }
  return out;
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Small components ──────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={statStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1.5rem",
  marginBottom: "1rem",
};
const subheaderStyle: React.CSSProperties = {
  color: "#666",
  margin: "0.25rem 0 0 0",
  fontSize: "0.9rem",
};
const statsStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.25rem",
  paddingTop: "0.3rem",
};
const statStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const statLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
};
const statValueStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 500,
};
const topActionsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 0",
  borderBottom: "1px solid #eee",
  marginBottom: "1rem",
};
const sectionHeaderStyle: React.CSSProperties = {
  marginTop: "2rem",
  marginBottom: "0.25rem",
  fontSize: "1.1rem",
  fontWeight: 600,
  color: "#222",
};
const sectionHelpStyle: React.CSSProperties = {
  margin: "0 0 0.75rem 0",
  color: "#666",
  fontSize: "0.85rem",
};
const tableSectionStyle: React.CSSProperties = {
  marginBottom: "1.5rem",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  overflow: "hidden",
};
const tableSectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.75rem",
  padding: "0.5rem 0.75rem",
  background: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
};
const tableSectionNameStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#1c4e80",
};
const tableSectionMetaStyle: React.CSSProperties = {
  fontSize: "0.78rem",
  color: "#64748b",
  fontVariantNumeric: "tabular-nums",
};
const tableWrapperStyle: React.CSSProperties = {
  overflowX: "auto",
};
const gridStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.85rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.5rem",
  background: "#fafafa",
  borderBottom: "2px solid #ddd",
  fontSize: "0.75rem",
  color: "#555",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const thMandatoryStyle: React.CSSProperties = {
  ...thStyle,
  color: "#0366d6",
};
const trStyle: React.CSSProperties = {};
const trTouchedStyle: React.CSSProperties = {
  background: "#fffde8",
};
const tdStyle: React.CSSProperties = {
  padding: "0.3rem 0.45rem",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};
const inputStyle: React.CSSProperties = {
  padding: "0.25rem 0.4rem",
  fontSize: "0.85rem",
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  width: "100%",
  minWidth: 100,
  boxSizing: "border-box",
};
const selectStyle: React.CSSProperties = {
  padding: "0.25rem 0.4rem",
  fontSize: "0.85rem",
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  background: "#fff",
};
const primaryButtonStyle: React.CSSProperties = {
  padding: "0.45rem 1rem",
  fontSize: "0.9rem",
  background: "#0366d6",
  color: "#fff",
  border: "1px solid #0366d6",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 500,
};
const primaryButtonDisabledStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: "#e0e0e0",
  borderColor: "#cbd5e0",
  color: "#888",
  cursor: "not-allowed",
};
const ghostButtonStyle: React.CSSProperties = {
  padding: "0.35rem 0.7rem",
  fontSize: "0.85rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  cursor: "pointer",
};
const linkButtonStyle: React.CSSProperties = {
  padding: 0,
  fontSize: "0.85rem",
  background: "transparent",
  color: "#0366d6",
  border: "none",
  cursor: "pointer",
  textDecoration: "underline",
};
const removeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#a02020",
  fontSize: "1.1rem",
  cursor: "pointer",
  padding: "0 0.4rem",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
};
const savedBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#e6f7e6",
  border: "1px solid #a5d8b8",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
  color: "#1a6b3a",
};
const noticeBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#fff8e6",
  border: "1px solid #f0d99b",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
  color: "#8a6d1a",
};
const emptyStateStyle: React.CSSProperties = {
  padding: "1.25rem 1.5rem",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  color: "#444",
};

// Chip-style multi-select for anchor lists.

const chipBoxStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  padding: 3,
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  background: "#fff",
  minWidth: 140,
  position: "relative",
};
const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  background: "#eef4ff",
  border: "1px solid #cdddf2",
  color: "#0366d6",
  padding: "1px 4px",
  borderRadius: 3,
  fontSize: "0.78rem",
};
const chipRemoveStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#0366d6",
  fontSize: "0.85rem",
  cursor: "pointer",
  padding: 0,
  lineHeight: 1,
};
const chipInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 60,
  border: "none",
  outline: "none",
  fontSize: "0.82rem",
  padding: "1px 2px",
};
const suggestionListStyle: React.CSSProperties = {
  position: "absolute",
  left: 0,
  top: "100%",
  zIndex: 10,
  background: "#fff",
  border: "1px solid #cbd5e0",
  borderTop: "none",
  borderRadius: "0 0 3px 3px",
  fontSize: "0.82rem",
  minWidth: "100%",
  maxHeight: 180,
  overflowY: "auto",
};
const suggestionItemStyle: React.CSSProperties = {
  padding: "3px 6px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f0f0",
};
